import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';

import { useTypedParams } from '@sorare/routing';

import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';

import { Matches as DumbMatches } from '@sorare/gameplay/src/components/seo/Matches';
import { PlayerGameScoreDialog } from '@sorare/gameplay/src/components/stats/PlayerGameScoreDialog';
import { anyGameToGenericGame } from '@sorare/gameplay/src/lib/anyGame';
import { anyPlayerGameStatsToGenericGame } from '@sorare/gameplay/src/lib/anyPlayerGameStats';

import {
  BaseballPlayerFutureFixturesQuery,
  BaseballPlayerFutureFixturesQueryVariables,
  BaseballPlayerPastFixturesQuery,
  BaseballPlayerPastFixturesQueryVariables,
} from './__generated__/page.graphql';
import { PageParams } from './__generated__/routeParams';

const BASEBALL_PLAYER_PAST_FIXTURES_QUERY = gql`
  query BaseballPlayerPastFixturesQuery($slug: String!) {
    anyPlayer(slug: $slug) {
      slug
      anyGameStats(last: 30) {
        id
        ...anyPlayerGameStatsToGenericGame_anyPlayerGameStats
      }
    }
  }
  ${anyPlayerGameStatsToGenericGame.fragments.anyPlayerGameStats}
` as TypedDocumentNode<
  BaseballPlayerPastFixturesQuery,
  BaseballPlayerPastFixturesQueryVariables
>;

const BASEBALL_PLAYER_FUTURE_FIXTURES_QUERY = gql`
  query BaseballPlayerFutureFixturesQuery($slug: String!) {
    anyPlayer(slug: $slug) {
      slug
      displayName
      activeClub {
        slug
        upcomingAnyGames(first: 30) {
          id
          ...anyGameToGenericGame_anyGameInterface
        }
      }
    }
  }
  ${anyGameToGenericGame.fragments.anyGameInterface}
` as TypedDocumentNode<
  BaseballPlayerFutureFixturesQuery,
  BaseballPlayerFutureFixturesQueryVariables
>;

export const PlayerGames = () => {
  const { slug } = useTypedParams<PageParams>();
  const [playerGameScoreId, setPlayerGameScoreId] = useState<string>();

  const { data: dataFuture, loading: loadingFuture } = usePaginatedQuery(
    BASEBALL_PLAYER_FUTURE_FIXTURES_QUERY,
    {
      variables: { slug },
      connection: 'GameConnection',
    }
  );

  const { data: dataPast, loading: loadingPast } = usePaginatedQuery(
    BASEBALL_PLAYER_PAST_FIXTURES_QUERY,
    {
      variables: { slug },
      connection: 'GameConnection',
    }
  );

  const pastPlayerGameStats = [
    ...(dataPast?.anyPlayer?.anyGameStats.filter(Boolean) || []),
  ].reverse();
  const futureGames =
    dataFuture?.anyPlayer?.activeClub?.upcomingAnyGames.filter(Boolean) || [];

  return (
    <>
      <DumbMatches
        displayName={dataFuture?.anyPlayer?.displayName ?? ''}
        loading={loadingFuture || loadingPast}
        pastGames={pastPlayerGameStats.map(playerGameStats =>
          anyPlayerGameStatsToGenericGame({
            playerGameStats,
            onClick: id => setPlayerGameScoreId(idFromObject(id)),
          })
        )}
        infiniteScrollLoaderPast={null}
        futureGames={futureGames.map(anyGame =>
          anyGameToGenericGame({ anyGame })
        )}
        infiniteScrollLoaderFuture={null}
      />
      {playerGameScoreId && (
        <PlayerGameScoreDialog
          playerGameScoreId={playerGameScoreId}
          onClose={() => setPlayerGameScoreId(undefined)}
          open={!!playerGameScoreId}
          showUpsell={false}
        />
      )}
    </>
  );
};

export default PlayerGames;
